import '@shoelace-style/shoelace/dist/components/spinner/spinner.js';

import {css, html, LitElement, PropertyValues} from 'lit';
import {customElement, query, state} from 'lit/decorators.js';
import {property} from 'lit/decorators.js';
import {repeat} from 'lit-html/directives/repeat.js';
import {translate} from '@stefanholzapfel/lit-translate';
import {classMap} from 'lit/directives/class-map.js';
import {SpImageService} from '../../services/sp-image.service';
import {when} from "lit/directives/when.js";
import {perfectScrollbar} from '../../styles/3rd-party/perfect-scrollbar.styles';
import {dispatchUpdateScrollbarsEvent, SpScrollbarController} from '../../controllers/sp-scrollbar.controller';
import {FabricImage, Canvas, Point, Group, util} from "fabric";
import {unsafeCSSIcon} from "../../styles/icons";

@customElement('sp-image-editor')
export class SpImageEditor extends LitElement {
    static styles =
        [
            perfectScrollbar,
            //language=css
            css`
                .container {
                    display: grid;
                    grid-template-rows: minmax(10rem, auto) max-content;
                    height: 100%;
                    width: 100%;
                }

                .spinner-container {
                    position: absolute;
                    top: 0;
                    left: 0;
                    z-index: 2;
                    width: 100%;
                    height: 100%;
                    background-color: rgb(var(--sp-greylight));
                    pointer-events: none;
                    display: grid;
                    place-content: center;
                }

                .sp-image-canvas-wrapper {
                    position: relative;
                    background-color: #000;
                }

                .sp-image-canvas-wrapper .canvas-icon {
                    position: absolute;
                    width: 40px;
                    height: 40px;
                    background-size: 100% auto;
                    background-repeat: no-repeat;
                    background-position: center center;
                    cursor: pointer;
                }

                .sp-image-canvas-wrapper .canvas-rotate {
                    right: 10px;
                    top: 10px;
                    background-image: url(${unsafeCSSIcon('rotate-right-circle', { color: 'grey-dark-filled', size: 'x128' })});
                }

                .sp-image-canvas-wrapper .canvas-trash {
                    left: 10px;
                    top: 10px;
                    background-image: url(${unsafeCSSIcon('trash-circle', { color: 'grey-dark-filled', size: 'x128' })});
                }

                .sp-image-canvas-wrapper.no-sticker .canvas-trash {
                    display: none;
                }

                .sp-image-canvas-wrapper .canvas-zoom-in {
                    right: 10px;
                    bottom: 60px;
                    background-image: url(${unsafeCSSIcon('plus-square', { color: 'grey-dark-filled', size: 'x128' })});
                }

                .sp-image-canvas-wrapper .canvas-zoom-out {
                    right: 10px;
                    bottom: 10px;
                    background-image: url(${unsafeCSSIcon('minus-square' ,{ color: 'grey-dark-filled', size: 'x128' })});
                }

                .sp-image-canvas .original {
                    width: 100%;
                    height: auto;
                    display: block;
                }

                .sp-image-sticker-wrapper {
                    position: relative;
                    background-color: #333432;
                    height: 150px;
                    margin-top: calc(var(--sp-default-padding) / 3);
                }

                .sp-image-sticker-wrapper.no-sticker {
                    display: none;
                }

                .sp-image-sticker-container {
                    width: auto;
                    height: 150px;
                    white-space: nowrap;
                }

                .sp-image-sticker {
                    display: inline-block;
                    position: relative;
                    min-width: 90px;
                    height: 150px;
                    text-align: center;
                    box-sizing: border-box;
                    padding: 130px 5px 5px 5px;
                }

                .sp-image-sticker .img-wrapper {
                    position: absolute;
                    bottom: 25px;
                    left: 0;
                    right: 0;
                }

                .sp-image-sticker img {
                    display: block;
                    margin: 0 auto;
                    max-height: 120px;
                    max-width: 80px;
                    cursor: pointer;
                }

                .sp-image-sticker span {
                    display: block;
                    width: 100%;
                    height: 15px;
                    color: #fff;
                    font-size: 10px;
                    line-height: 15px;
                    text-align: center;
                }
                
                sl-spinner {
                    font-size: 7rem;
                    --track-width: calc(var(--sp-default-padding) / 2);
                }
                
                .no-opacity {
                    opacity: 0;
                }`
        ];

    @property({type: Object})
    image: string | File | Blob | Canvas;
    @property({type: Object})
    stickers: SpStickerConfig[];
    @property({type: Promise})
    delayUntil

    @state()
    loading = true;
    @state()
    hasSelection = false;

    @query('#canvas-wrapper')
    canvasWrapper;

    private canvas: Canvas;
    private panning = false;
    private lastPanningPosition;
    private minXCenter = 0;
    private maxXCenter = 0;
    private minYCenter = 0;
    private maxYCenter = 0;
    private canvasImage;
    private saveEditorEvent: EditorSavedEvent = new CustomEvent(
        'save-editor',
        {
            bubbles: true,
            composed: true,
            detail: {
                image: null,
                canvasData: null
            }
        }
    );
    private scrollbarController = new SpScrollbarController(this);

    disconnectedCallback() {
        this.shadowRoot.querySelectorAll('.img-wrapper').forEach(node => {
            (node as HTMLElement).removeEventListener('click', this.stickerClick);
        });
        window.removeEventListener('resize', this.autoScaleAll.bind(this));
        super.disconnectedCallback();
    }

    protected firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        const stickerWrapper = this.shadowRoot.getElementById('sticker-wrapper');
        if (stickerWrapper) {
            this.scrollbarController.add(stickerWrapper, {psOptions: {suppressScrollY: true}});
        }
        this.canvas = new Canvas(this.shadowRoot.getElementById('canvas') as HTMLCanvasElement, {
            width: 0,
            height: 0,
            backgroundColor: '#000000',
            selection: false,
            enableRetinaScaling: false
        });
        this.canvas.on('mouse:up', event => {
            this.panning = false;
        });
        this.canvas.on('mouse:down', event => {
            this.panning = true;
            this.lastPanningPosition = event.pointer;
        });
        this.canvas.on('mouse:move', event => {
            let center = this.canvas.getVpCenter();
            let object = this.canvas.getActiveObject();
            if ((typeof object == 'undefined' || !object) && this.panning && event && event.e) {
                let panX = event.pointer.x - this.lastPanningPosition.x;
                if ((panX < 0 && center.x >= this.maxXCenter) || (panX > 0 && center.x <= this.minXCenter)) panX = 0;
                if (panX < 0 && center.x - panX > this.maxXCenter) panX = (this.maxXCenter - center.x) * -1;
                else if (panX > 0 && center.x - panX < this.minXCenter) panX = center.x - this.minXCenter;

                let panY = event.pointer.y - this.lastPanningPosition.y;
                if ((panY < 0 && center.y >= this.maxYCenter) || (panY > 0 && center.y <= this.minYCenter)) panY = 0;
                if (panY < 0 && center.y - panY > this.maxYCenter) panY = (this.maxYCenter - center.y) * -1;
                else if (panY > 0 && center.y - panY < this.minYCenter) panY = center.y - this.minYCenter;

                let delta = new Point(panX, panY);
                this.canvas.relativePan(delta);
                this.lastPanningPosition = event.pointer;
            }
        });
        this.canvas.on('selection:created', event => {
            this.hasSelection = true;
        });
        this.canvas.on('selection:cleared', event => {
            this.hasSelection = false;
        });
        this.shadowRoot.querySelectorAll('.img-wrapper img').forEach(node => {
            (node as HTMLElement).addEventListener('click', this.stickerClick.bind(this));
        });
        window.addEventListener('resize', this.autoScaleAll.bind(this));
    }

    protected updated(_changedProperties: PropertyValues) {
        super.updated(_changedProperties);
        if (_changedProperties.has('image')) {
            this.loading = true;
            if (this.delayUntil) {
                this.delayUntil.then(() => {
                    this.load()
                });
            } else {
                this.load();
            }
        }
    }

    public async load() {
        if (!(this.image instanceof Blob) && !(typeof this.image === 'string')) {
            await this.canvas.loadFromJSON(this.image);
            this.canvas.forEachObject((obj) => {
                if (obj.get("id") === 'original') {
                    this.canvasImage = obj;
                }
            });
        } else {
            this.canvas.clear();
            let url = this.image;
            if (this.image instanceof Blob)
                url = await SpImageService.blobToBase64(this.image);
            this.canvasImage = await FabricImage.fromURL(url as string,  {
                crossOrigin: 'anonymous'
            });
            this.canvas.add(this.canvasImage);
            this.canvasImage.set({
                id: 'original',
                selectable: false,
                hoverCursor: 'default',
            });
        }
        this.autoScaleAll();
        this.updatePanLimits();
        this.loading = false;
        const stickerWrapper = this.shadowRoot.getElementById('sticker-wrapper');
        if (stickerWrapper)
            dispatchUpdateScrollbarsEvent(stickerWrapper, true);
    }

    render() {
        return html`
            ${
                when(this.loading, () => html`
                    <div class="spinner-container">
                        <sl-spinner></sl-spinner>
                    </div>`)
            }
            <div class=${ classMap({
                'container': true,
                'no-opacity': this.loading,
            })}>
                <div id="canvas-wrapper"
                     class="sp-image-canvas-wrapper">
                    <canvas id="canvas"></canvas>
                    <div @click="${this.rotateAll}" class="canvas-icon canvas-rotate"></div>
                    ${ when(this.hasSelection, () => html`
                        <div @click="${this.removeActive}" class="canvas-icon canvas-trash"></div>`)}
                    <div @click="${this.zoomIn}" class="canvas-icon canvas-zoom-in"></div>
                    <div @click="${this.zoomOut}" class="canvas-icon canvas-zoom-out"></div>
                </div>
                ${when(this.stickers && this.stickers.length > 0,
                        () => html`
                            <div
                                id="sticker-wrapper"
                                class="${
                                        classMap({
                                            'sp-image-sticker-wrapper': true,
                                            'no-sticker': this.stickers?.length === 0
                                        })}">
                                <div id="images" class="sp-image-sticker-container">
                                    ${
                                            repeat(this.stickers, sticker => {
                                                return html`
                                                    <div class="sp-image-sticker">
                                                        <div class="img-wrapper">
                                                            <img
                                                                    src="${sticker.imageUrl}"
                                                                    id="${sticker.id}"/>
                                                        </div>
                                                        <span>${translate(sticker.translationString)}</span>
                                                    </div>
                                                `;
                                            })
                                    }
                                </div>
                            </div>`)
                }
            </div>
        `;
    }

    async stickerClick(event: Event) {
        const clickedElem = (event.currentTarget as HTMLImageElement);
        let center = this.canvas.getVpCenter();
        let width = clickedElem.offsetWidth;
        let height = clickedElem.offsetHeight;
        let sticker;
        for (const _sticker of this.stickers) {
            if (clickedElem.id === _sticker.id) sticker = _sticker;
        }
        let options = {
            left: center.x - (width / this.canvas.getZoom()) / 2,
            top: center.y - (height / this.canvas.getZoom()) / 2,
            lockScalingFlip: true,
            cornerColor: '#ffffff',
            borderColor: '#ffffff',
            borderScaleFactor: 2
        };

        if (sticker?.options) {
            options = {
                ...options,
                ...sticker.options
            }
        }

        const img = await FabricImage.fromURL(clickedElem.src);
        img.set(options);
        if (options.hasOwnProperty('lockRotation') && (options as any).lockRotation)
            img.setControlVisible('mtr', false);
        img.setControlVisible('ml', false);
        img.setControlVisible('mr', false);
        img.setControlVisible('mt', false);
        img.setControlVisible('mb', false);
        img.scaleToWidth(width / this.canvas.getZoom());
        img.scaleToHeight(height / this.canvas.getZoom());
        this.canvas.add(img);
        this.canvas.setActiveObject(img);
    }

    rotateAll() {
        const selected = this.canvas.getActiveObject();
        const objects = this.canvas.getObjects();
        this.canvas.clear();
        const group = new Group(objects);
        group.rotate(90);
        group.getObjects().forEach((obj) => {
            group.remove(obj);
            obj.setCoords();
            this.canvas.add(obj);
        });
        group.dispose();
        if (selected)
           this.canvas.setActiveObject(selected);

        this.canvas.renderAll();

        this.autoScaleAll();
        this.updatePanLimits();
    }

    removeActive() {
        let object = this.canvas.getActiveObject();
        if (object) {
            this.canvas.remove(object);
        }
    }

    zoomIn() {
        let zoom = this.canvas.getZoom();
        if (zoom < 2) {
            let point = new Point(this.canvas.width / 2, this.canvas.height / 2);
            this.canvas.zoomToPoint(point, zoom + 0.2);
            this.updatePanLimits();
        }
    }

    zoomOut() {
        let zoom = this.canvas.getZoom();
        if (zoom > 1) {
            let point = new Point(this.canvas.width / 2, this.canvas.height / 2);
            this.canvas.zoomToPoint(point, zoom - 0.2);

            zoom = this.canvas.getZoom();
            let center = this.canvas.getVpCenter();
            let panX = 0, panY = 0;
            let img = this.canvasImage.getBoundingRect(true);

            let spaceL = img.left - (center.x - (this.canvas.getWidth() / 2) / zoom);
            let spaceR = (center.x + (this.canvas.getWidth() / 2) / zoom) - (img.left + img.width);
            if (spaceL > 0 && (spaceL + spaceR) < 0) panX = -spaceL;
            else if (spaceR > 0 && (spaceR + spaceL) < 0) panX = spaceR;
            else if (spaceL > 0 || spaceR > 0) panX = ((this.canvas.getWidth() / 2) - center.x) * -1;

            let spaceB = (center.y + (this.canvas.getHeight() / 2) / zoom) - (img.top + img.height);
            let spaceT = img.top - (center.y - (this.canvas.getHeight() / 2) / zoom);
            if (spaceB > 0 && (spaceB + spaceT) < 0) panY = spaceB;
            else if (spaceT > 0 && (spaceT + spaceB) < 0) panY = -spaceT;
            else if (spaceB > 0 || spaceT > 0) panY = ((this.canvas.getHeight() / 2) - center.y) * -1;

            let delta = new Point(panX * zoom, panY * zoom);
            this.canvas.relativePan(delta);
            this.updatePanLimits();
        }
    }

    updatePanLimits() {
        let img = this.canvasImage.getBoundingRect(true);

        this.minXCenter = (this.canvas.getWidth() / 2);
        if ((this.canvas.getWidth() / this.canvas.getZoom()) < img.width)
            this.minXCenter -= (img.width - this.canvas.getWidth() / this.canvas.getZoom()) / 2;
        this.maxXCenter = this.canvas.getWidth() - this.minXCenter;

        this.minYCenter = (this.canvas.getHeight() / 2);
        if ((this.canvas.getHeight() / this.canvas.getZoom()) < img.height)
            this.minYCenter -= (img.height - this.canvas.getHeight() / this.canvas.getZoom()) / 2;
        this.maxYCenter = this.canvas.getHeight() - this.minYCenter;
    }

    autoScaleImage() {
        this.canvas.setZoom(1);
        let point = new Point(0, 0);
        this.canvas.absolutePan(point);

        let img = this.canvasImage.getBoundingRect(true);

        let ratioVP = this.canvas.getWidth() / this.canvas.getHeight();
        let ratioOrg = img.width / img.height;

        if (ratioVP < ratioOrg) this.canvasImage.scaleToWidth(this.canvas.getWidth());
        else this.canvasImage.scaleToHeight(this.canvas.getHeight());

        this.canvas.centerObject(this.canvasImage);
    }

    autoScaleAll() {
        let oldOrg = this.canvasImage.getBoundingRect(true);
        let oldOrgCenter = this.canvasImage.getCenterPoint();
        let oldOrgWidth = oldOrg.width;
        let oldOrgHeight = oldOrg.height;

        const size = this.canvasWrapper.getBoundingClientRect();
        this.canvas.setDimensions({
            height: size.height,
            width: size.width
        });
        this.autoScaleImage();
        this.updatePanLimits();

        let newOrg = this.canvasImage.getBoundingRect(true);
        let newOrgCenter = this.canvasImage.getCenterPoint();
        let newOrgWidth = newOrg.width;
        let newOrgHeight = newOrg.height;

        this.canvas.forEachObject(obj => {
            if (typeof obj.get("id") == 'undefined' || (obj.get("id") && obj.get("id") !== 'original')) {

                let objCenter = obj.getCenterPoint();
                let leftPer = (oldOrgCenter.x - objCenter.x) / (oldOrgWidth / 2);
                let topPer = (oldOrgCenter.y - objCenter.y) / (oldOrgHeight / 2);

                obj.set({centeredScaling: true});
                obj.scale(obj.scaleX * (newOrgHeight / oldOrgHeight));
                obj.set({centeredScaling: false});

                let leftPan = (newOrgCenter.x - objCenter.x) - (newOrgWidth / 2) * leftPer;
                let topPan = (newOrgCenter.y - objCenter.y) - (newOrgHeight / 2) * topPer;

                let newCenter = new Point(objCenter.x + leftPan, objCenter.y + topPan);

                obj.setPositionByOrigin(newCenter, 'center', 'center');
                obj.setCoords();
            }
        });
        this.canvas.renderAll();
    }

    async save(): Promise<ImageEditorData> {
        let img = this.canvasImage.getBoundingRect(true);
        let scale = this.canvasImage.scaleX;
        let dataURL = this.canvas.toDataURL({
            format: 'jpeg',
            quality: 0.75,
            multiplier: 1 / scale,
            left: img.left,
            top: img.top,
            width: img.width,
            height: img.height,
        });

        this.saveEditorEvent.detail.image = SpImageService.dataURLtoBlob(dataURL);
        this.saveEditorEvent.detail.canvasData = await this.canvas.toObject([
            'selectable',
            'hoverCursor',
            'lockScalingFlip',
            'cornerColor',
            'borderColor',
            'borderScaleFactor',
            'lockRotation',
            'hasRotatingPoint',
            'width',
            'height',
            'scaleX',
            'scaleY',
            'left',
            'top',
            'angle',
            'id',
            '_controlsVisibility'
        ]);
        this.dispatchEvent(this.saveEditorEvent);
        return this.saveEditorEvent.detail;
    }
}

export interface SpStickerConfig {
    id: string;
    translationString: string;
    imageUrl: string;
}

export interface EditorSavedEvent extends Event {
    detail: ImageEditorData;
}

export interface ImageEditorData {
    image: Blob;
    canvasData: object;
}
